import { Instance } from '../types/api'
import { colors, spacing, borderRadius, shadows, typography, transitions } from '../styles/tokens'

interface InstanceCardProps {
  instance: Instance
  index: number
}

export function InstanceCard({ instance, index }: InstanceCardProps) {
  return (
    <div style={{
      backgroundColor: colors.background.card,
      border: `1px solid ${colors.border.main}`,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      display: 'flex',
      alignItems: 'center',
      gap: spacing.xl,
      transition: transitions.fast,
      boxShadow: shadows.md,
    }}>
      {/* Profile Picture Section */}
      <div style={{ 
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: spacing.sm
      }}>
        {instance.Line?.Picture ? (
          <img 
            src={`data:image/png;base64,${instance.Line.Picture}`}
            alt={`Profile picture of ${instance.Line.Name || 'User'}`}
            style={{ 
              width: '70px', 
              height: '70px', 
              borderRadius: borderRadius.full, 
              border: `3px solid ${colors.primary}`,
              objectFit: 'cover'
            }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div style={{
            width: '70px',
            height: '70px',
            borderRadius: borderRadius.full,
            backgroundColor: colors.background.button,
            border: `3px solid ${colors.primary}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            color: colors.text.muted
          }}>
            👤
          </div>
        )}
      </div>

      {/* Main Content */}
      <div style={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.md
      }}>
        {/* Header: RAuser prominente + Status */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.lg }}>
            {/* RAuser como protagonista */}
            <h3 style={{ 
              margin: 0, 
              color: colors.text.accent, 
              fontSize: typography.fontSize['2xl'],
              fontWeight: typography.fontWeight.bold
            }}>
              {instance.RAuser || 'Unknown User'}
            </h3>
            
            {/* Instance name pequeño y discreto */}
            <span style={{ 
              color: colors.text.disabled, 
              fontSize: typography.fontSize.sm,
              fontStyle: 'italic'
            }}>
              ({instance.Instance || `Instance ${index + 1}`})
            </span>
          </div>

          {/* Status badge */}
          <span style={{ 
            padding: `${spacing.sm} ${spacing.lg}`, 
            borderRadius: borderRadius.pill, 
            fontSize: typography.fontSize.sm, 
            fontWeight: typography.fontWeight.bold,
            backgroundColor: instance.Status === 'Connected' ? colors.status.connected : colors.status.disconnected,
            color: colors.text.primary,
            textTransform: 'uppercase',
            letterSpacing: typography.letterSpacing.tight
          }}>
            {instance.Status || 'Unknown'}
          </span>
        </div>

        {/* Información secundaria en una línea compacta */}
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          gap: spacing.xl,
          color: colors.text.muted,
          fontSize: typography.fontSize.sm,
          flexWrap: 'wrap'
        }}>
          {instance.Line?.Name && (
            <span>
              <strong style={{ color: colors.text.tertiary }}>Name:</strong> {instance.Line.Name}
            </span>
          )}
          {instance.Line?.Phone && (
            <span style={{ fontFamily: 'monospace' }}>
              <strong style={{ color: colors.text.tertiary }}>Phone:</strong> {instance.Line.Phone}
            </span>
          )}
          {instance.Line?.Account && (
            <span style={{ 
              color: instance.Line.Account === 'Business' ? colors.status.warning : colors.status.info,
              fontWeight: typography.fontWeight.bold
            }}>
              {instance.Line.Account}
            </span>
          )}
          {instance.Webhook && (
            <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              <strong style={{ color: colors.text.tertiary }}>Webhook:</strong> 
              <span style={{ color: colors.status.webhook, marginLeft: spacing.xs }}>
                {instance.Webhook}
              </span>
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
